import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export const useCompanyGrowthStore = create(
  persist(
    (set, get) => ({
      // Company Info
      company: {
        name: 'Cybertection',
        founded: '2022',
        stage: 'Growth Stage',
        employees: 42,
      },

      // Core Metrics
      metrics: {
        revenue: {
          current: 129000,
          lastMonth: 115000,
          lastYear: 45000,
          growth: 12.2,
        },
        customers: {
          total: 234,
          new: 18,
          churn: 2,
          retention: 99.1,
          ltv: 5500,
          cac: 480,
        },
        market: {
          marketSize: 15000000,
          targetableMarket: 500000,
          marketShare: 0.047,
          gartnerMagicQuadrant: 'Challenger',
        },
        team: {
          total: 42,
          engineering: 14,
          sales: 8,
          marketing: 6,
          operations: 8,
          support: 6,
          hiring_plan: 12,
        },
        funding: {
          raised: 500000,
          runway_months: 18,
          burn_rate: 28000,
          next_round: 'Series A',
        },
      },

      // Products
      products: [
        { id: 1, name: 'Training Platform', revenue: 52000, customers: 145, growth: 18 },
        { id: 2, name: 'Consulting Services', revenue: 45000, customers: 42, growth: 14 },
        { id: 3, name: 'Cloud Security', revenue: 28000, customers: 35, growth: 25 },
        { id: 4, name: 'Training Courses', revenue: 12000, customers: 12, growth: 8 },
      ],

      // Monthly Data
      monthlyData: [
        { id: 1, month: 'Jan', revenue: 85000, customers: 156, churn: 1 },
        { id: 2, month: 'Feb', revenue: 92000, customers: 168, churn: 1 },
        { id: 3, month: 'Mar', revenue: 98000, customers: 184, churn: 2 },
        { id: 4, month: 'Apr', revenue: 105000, customers: 198, churn: 1 },
        { id: 5, month: 'May', revenue: 115000, customers: 216, churn: 3 },
        { id: 6, month: 'Jun', revenue: 129000, customers: 234, churn: 2 },
      ],

      // Opportunities
      opportunities: [
        {
          id: 1,
          title: 'Enterprise Market Penetration',
          potential: '$2.5M ARR',
          timeline: '12-18 months',
          resources: 'Sales team + partnerships',
          likelihood: 'High',
          status: 'In Progress',
          createdAt: new Date().toISOString(),
        },
        {
          id: 2,
          title: 'New Product Line: Compliance',
          potential: '$800K ARR',
          timeline: '6-9 months',
          resources: '2 engineers + product',
          likelihood: 'Medium-High',
          status: 'Planning',
          createdAt: new Date().toISOString(),
        },
        {
          id: 3,
          title: 'International Expansion',
          potential: '$1.2M ARR',
          timeline: '18-24 months',
          resources: 'Regional sales + localization',
          likelihood: 'Medium',
          status: 'Research',
          createdAt: new Date().toISOString(),
        },
        {
          id: 4,
          title: 'Strategic Partnerships',
          potential: '$600K ARR',
          timeline: '3-6 months',
          resources: 'Partnership development',
          likelihood: 'High',
          status: 'In Progress',
          createdAt: new Date().toISOString(),
        },
      ],

      // Risks
      risks: [
        {
          id: 1,
          risk: 'Market competition increasing',
          impact: 'High',
          mitigation: 'Differentiation via ML',
          status: 'Monitoring',
          createdAt: new Date().toISOString(),
        },
        {
          id: 2,
          risk: 'Customer churn acceleration',
          impact: 'Medium',
          mitigation: 'NPS focus + retention',
          status: 'Active',
          createdAt: new Date().toISOString(),
        },
        {
          id: 3,
          risk: 'Talent acquisition delays',
          impact: 'Medium',
          mitigation: 'Recruiting budget increase',
          status: 'Monitoring',
          createdAt: new Date().toISOString(),
        },
        {
          id: 4,
          risk: 'Funding environment',
          impact: 'Medium',
          mitigation: 'Extend runway to 24 months',
          status: 'Monitoring',
          createdAt: new Date().toISOString(),
        },
      ],

      // AI Insights (updated by AI)
      aiInsights: [
        'Strong growth trajectory: 51.8% YTD with 12.2% MoM acceleration',
        'Cloud Security showing 25% growth - allocate more resources',
        'Enterprise ready: 99.1% retention and strong LTV suggests enterprise opportunity',
        'Runway action: 18-month runway is healthy but aim for Series A in 6-9 months',
      ],

      // Actions
      updateCompanyInfo: (newInfo) =>
        set((state) => ({
          company: { ...state.company, ...newInfo },
        })),

      updateMetric: (metricType, metricKey, value) =>
        set((state) => ({
          metrics: {
            ...state.metrics,
            [metricType]: {
              ...state.metrics[metricType],
              [metricKey]: value,
            },
          },
        })),

      // Products
      addProduct: (product) =>
        set((state) => ({
          products: [
            ...state.products,
            {
              ...product,
              id: Math.max(...state.products.map((p) => p.id), 0) + 1,
            },
          ],
        })),

      updateProduct: (productId, updates) =>
        set((state) => ({
          products: state.products.map((p) =>
            p.id === productId ? { ...p, ...updates } : p
          ),
        })),

      deleteProduct: (productId) =>
        set((state) => ({
          products: state.products.filter((p) => p.id !== productId),
        })),

      // Monthly Data
      addMonthData: (monthData) =>
        set((state) => ({
          monthlyData: [
            ...state.monthlyData,
            {
              ...monthData,
              id: Math.max(...state.monthlyData.map((m) => m.id), 0) + 1,
            },
          ],
        })),

      updateMonthData: (monthId, updates) =>
        set((state) => ({
          monthlyData: state.monthlyData.map((m) =>
            m.id === monthId ? { ...m, ...updates } : m
          ),
        })),

      deleteMonthData: (monthId) =>
        set((state) => ({
          monthlyData: state.monthlyData.filter((m) => m.id !== monthId),
        })),

      // Opportunities
      addOpportunity: (opportunity) =>
        set((state) => ({
          opportunities: [
            ...state.opportunities,
            {
              ...opportunity,
              id: Math.max(...state.opportunities.map((o) => o.id), 0) + 1,
              createdAt: new Date().toISOString(),
            },
          ],
        })),

      updateOpportunity: (opportunityId, updates) =>
        set((state) => ({
          opportunities: state.opportunities.map((o) =>
            o.id === opportunityId ? { ...o, ...updates } : o
          ),
        })),

      deleteOpportunity: (opportunityId) =>
        set((state) => ({
          opportunities: state.opportunities.filter((o) => o.id !== opportunityId),
        })),

      // Risks
      addRisk: (risk) =>
        set((state) => ({
          risks: [
            ...state.risks,
            {
              ...risk,
              id: Math.max(...state.risks.map((r) => r.id), 0) + 1,
              createdAt: new Date().toISOString(),
            },
          ],
        })),

      updateRisk: (riskId, updates) =>
        set((state) => ({
          risks: state.risks.map((r) =>
            r.id === riskId ? { ...r, ...updates } : r
          ),
        })),

      deleteRisk: (riskId) =>
        set((state) => ({
          risks: state.risks.filter((r) => r.id !== riskId),
        })),

      // AI Insights
      addAIInsight: (insight) =>
        set((state) => ({
          aiInsights: [...state.aiInsights, insight],
        })),

      updateAIInsights: (insights) =>
        set(() => ({
          aiInsights: insights,
        })),

      // Get all data for AI analysis
      getFullAnalyticsData: () => {
        const state = get()
        return {
          company: state.company,
          metrics: state.metrics,
          products: state.products,
          monthlyData: state.monthlyData,
          opportunities: state.opportunities,
          risks: state.risks,
          aiInsights: state.aiInsights,
        }
      },

      // Calculate derived metrics
      getGrowthMetrics: () => {
        const state = get()
        const revenueGrowth = (
          ((state.metrics.revenue.current - state.metrics.revenue.lastMonth) /
            state.metrics.revenue.lastMonth) *
          100
        ).toFixed(1)
        const customerGrowth = (
          ((state.metrics.customers.total -
            state.monthlyData[0].customers) /
            state.monthlyData[0].customers) *
          100
        ).toFixed(1)
        const arr = state.metrics.revenue.current * 12
        const mrr = state.metrics.revenue.current

        return {
          arr,
          mrr,
          revenueGrowth,
          customerGrowth,
          acvMrr: (mrr / state.metrics.customers.total).toFixed(0),
          ltv: state.metrics.customers.ltv,
          cac: state.metrics.customers.cac,
          ltv_cac: (state.metrics.customers.ltv / state.metrics.customers.cac).toFixed(1),
        }
      },
    }),
    {
      name: 'company-growth-store',
      getStorage: () => localStorage,
    }
  )
)
